import React from "react";
import { api } from "../lib/api";
import PropertyCard from "./PropertyCard";
import { useT } from "../lib/i18n";

export default function AiSearchPanel() {
  const { t, lang } = useT();
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [matches, setMatches] = React.useState([]);
  const scrollRef = React.useRef(null);

  const examples = [
    t("ai.ex_1"), t("ai.ex_2"), t("ai.ex_3"), t("ai.ex_4"),
  ];

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  const submit = async (q) => {
    const txt = (q || query).trim();
    if (!txt || loading) return;
    setLoading(true);
    setHistory((h) => [...h, { role: "user", text: txt }]);
    setQuery("");
    try {
      const res = await api.post("/ai/search", { query: txt, lang });
      setHistory((h) => [...h, { role: "ai", text: res.data.answer }]);
      setMatches(res.data.matches || []);
    } catch (e) {
      setHistory((h) => [
        ...h,
        { role: "ai", text: `> ERROR: unable to reach inventory. ${e?.message || ""}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="ai-search-section" className="py-20 bg-zinc-50 border-y border-zinc-200">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-3">{t("ai.kicker")}</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-5">
              {t("ai.title_a")}
              <br />{t("ai.title_b")}
              <br /><span className="text-[#031833]">{t("ai.title_c")}</span>
            </h2>
            <p className="text-zinc-600 leading-relaxed mb-6 max-w-md">
              {t("ai.sub")}
            </p>
            <div className="space-y-2">
              <div className="font-mono-tech text-[10px] uppercase text-zinc-500">{t("ai.try")}</div>
              {examples.map((ex, i) => (
                <button
                  key={i}
                  data-testid={`ai-example-${i}`}
                  onClick={() => submit(ex)}
                  className="block text-left text-sm w-full border-l-2 border-zinc-200 hover:border-[#031833] pl-3 py-1 text-zinc-700 hover:text-zinc-900 transition-colors"
                >
                  → {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="terminal border border-zinc-900" data-testid="ai-terminal">
              <div className="border-b border-zinc-800 px-4 py-2 flex items-center gap-2 bg-black/40">
                <span className="w-3 h-3 bg-red-500"></span>
                <span className="w-3 h-3 bg-yellow-500"></span>
                <span className="w-3 h-3 bg-green-500"></span>
                <span className="ml-auto font-mono-tech text-[10px] text-zinc-400 uppercase">
                  warehousesuae@dubai:~/inventory
                </span>
              </div>
              <div ref={scrollRef} className="p-4 h-[360px] overflow-y-auto font-mono-tech text-xs leading-relaxed">
                {history.length === 0 && (
                  <div className="text-zinc-500">
                    <div>$ warehousesuae --connect inventory</div>
                    <div>✓ 247 active listings loaded</div>
                    <div>✓ AI matching engine ready</div>
                    <div className="mt-3 text-zinc-400">$ awaiting query<span className="cursor-blink"></span></div>
                  </div>
                )}
                {history.map((m, i) => (
                  <div key={i} className="mb-3">
                    {m.role === "user" ? (
                      <div>
                        <span className="text-[#22C55E]">$</span> <span className="text-white">{m.text}</span>
                      </div>
                    ) : (
                      <div className="text-zinc-300 whitespace-pre-wrap">{m.text}</div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="text-zinc-400">
                    <span className="text-[#FFCC00]">›</span> analysing inventory<span className="cursor-blink"></span>
                  </div>
                )}
              </div>
              <div className="border-t border-zinc-800 p-2 flex gap-2">
                <input
                  data-testid="ai-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder={t("ai.placeholder")}
                  className="flex-1 bg-transparent border-none outline-none text-white font-mono-tech text-xs placeholder-zinc-600 px-2"
                />
                <button
                  data-testid="ai-search-submit"
                  onClick={() => submit()}
                  disabled={loading}
                  className="bg-[#031833] text-white font-mono-tech text-[10px] uppercase px-4 py-2 disabled:opacity-50 hover:bg-[#0A2455]"
                >
                  {loading ? "..." : t("ai.send")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {matches.length > 0 && (
          <div className="mt-10">
            <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mb-4">
              {t("ai.matches")} — {matches.length}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.slice(0, 6).map((m, idx) => (
                <PropertyCard key={m.id} property={m} index={idx} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
